import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { PLAN_PRICES, ADDON_PRICES } from '@/config/pricing';
import { PLAN_CARDS } from '@/config/planFeatures';
import { PlanComparisonTable } from '@/components/PlanComparisonTable';
import { SAMPLE_REPORT_PUBLISHED, SAMPLE_REPORT_LINK_TEXT } from '@/pages/sampleReportContent';
import '@/styles/forms.css';
import '@/styles/landing.css';

/**
 * /pricing — a real page, as of 2026-08-31.
 *
 * ── IT WAS A 301 FOR MONTHS, AND THAT WAS THE WRONG SHAPE ───────────────────
 *
 * `nginx.conf` redirected /pricing and /pricing/ to /ux-auditor/#pricing,
 * because the plans lived in a section of another page. The redirect was an
 * improvement on the 404 it replaced, and it is still the wrong answer for the
 * one URL a buyer types and a search engine guesses: an anchor on a marketing
 * page cannot carry its own title, its own meta description, or its own
 * canonical, so /pricing could never rank for the query it is named after.
 *
 * The nav and the footer both label a link "Pricing" and both now point here.
 *
 * ── EVERY NUMBER IS READ, NONE IS TYPED ─────────────────────────────────────
 *
 * PLAN_PRICES and ADDON_PRICES, including the JSON-LD Offer prices. A page
 * about pricing is the last place a literal should survive a repricing, and
 * src/__tests__/no-bare-price-literal.test.js fails the build if one appears.
 *
 * ── WHAT THIS PAGE MAY NOT CLAIM ────────────────────────────────────────────
 *
 * Two plans can be bought: FREE and STARTER, neither a subscription. Agency is
 * shown as a third card but is arranged by hand, billed monthly by agreement
 * (CONTACT_TIERS). There is no Pro, no Max, no Enterprise: those recurring
 * tiers were parked on 2026-08-31 with
 * four paying customers on the books, and the parking is recorded in
 * docs/analysis/open-items.md with its re-open condition. Nothing here hints
 * at a tier that cannot be bought; the check-limit upsell used to quote PRO
 * and MAX prices for products that did not exist, and that is the mistake this
 * page is closest to repeating.
 */
export default function PricingPage() {
  const { login, user } = useAuth();

  /* THE CARDS ARE PLAN_CARDS (config/planFeatures.js), the same list
     /ux-auditor/ renders, so the two pages cannot show different plans again
     (owner, 2026-10-07). Bullets come from cardBullets, which the comparison
     table below reads too. */

  return (
    <div className="uxa-landing">
      <SEO
        /* fullTitle, not title, because " | UXAuditPro" is already on the end of
           this one and `title` would append a second copy.

           Was "Pricing | UXAuditPro" (20 chars). This is the commercial page and
           it was carrying none of the query cluster it should own: the
           free/pricing cluster sits at 241 impressions, position 56.7, zero
           clicks. "Pricing" alone matches nothing anyone searches for. */
        fullTitle="UX Audit Cost & Pricing: 1 Free Audit a Month | UXAuditPro"
        description={
          `How much does a UX audit cost? One free audit every month, then `
          + `$${PLAN_PRICES.STARTER.usd} per audit. Free and Starter have no subscription. See what each report includes.`
        }
        canonical="/pricing"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'UXAuditPro',
          description:
            'AI-powered UX audit tool that scores a page across 6 dimensions and '
            + 'returns every finding with the evidence behind it.',
          brand: { '@type': 'Brand', name: 'UXAuditPro' },
          // Prices come from PLAN_PRICES. Schema.org wants strings, so they are
          // cast rather than typed -- the same rule the /ux-auditor/ Offer
          // block was moved onto, and for the same reason: a stale price in
          // structured data is the number Google puts in front of a buyer.
          offers: [
            {
              '@type': 'Offer',
              name: 'Free',
              price: String(PLAN_PRICES.FREE.usd),
              priceCurrency: 'USD',
              description: `1 page, ${PLAN_PRICES.FREE.quota}`,
            },
            {
              '@type': 'Offer',
              name: 'Starter',
              price: String(PLAN_PRICES.STARTER.usd),
              priceCurrency: 'USD',
              description: 'Up to 3 pages of the same site, per audit',
            },
          ],
        }}
      />

      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="uxa-hero-eyebrow">One free audit every month &middot; Pay per audit &middot; Agency plans by arrangement</span>
        <h1 className="uxa-h1" style={{ margin: 'var(--space-6) auto 0' }}>Pricing</h1>
        <p className="uxa-lead" style={{ maxWidth: 620, margin: 'var(--space-5) auto 0', color: 'var(--ink-2)' }}>
          You get one free audit every month. After that you pay per audit, once.
          Free and Starter have no subscription.
        </p>
      </section>

      <section className="uxa-landing-section uxa-pt-xl">
        {/* A REAL h2 for the plan grid, and the plan names are h3.
            The first cut made each plan name an <h2 className="uxa-h3">, and
            check-type-scale-by-surface caught it: 24px is on the scale, but it
            is h3's size, and the UI zone allows h2 only at 38. The guard's own
            words -- "a size can be on the scale and still be on the WRONG
            scale". Fixing it by enlarging the plan names would have been the
            wrong direction; the page was missing a heading level, not a size. */}
        <h2 className="uxa-h2" style={{ textAlign: 'center', margin: '0 0 var(--space-12)' }}>
          Three plans
        </h2>
        {/* THREE CARDS ARE ROOMIER THAN TWO WERE, not tighter. The 720px cap
            was what squeezed them: it gave each of two cards 349px. At the
            section's full 1136px of content, three columns on --space-6 gaps
            give 364px each, so inner content goes from 261px to 276px. No type
            or padding was shrunk to fit, because nothing needed to be. */}
        <div className="uxa-grid-3" style={{ alignItems: 'start' }}>
          {PLAN_CARDS.map((plan) => (
            <div
              key={plan.name}
              className={`uxa-price-card${plan.highlighted ? ' uxa-price-card--featured' : ''} relative flex flex-col h-full`}
              style={{ textAlign: 'left' }}
              data-testid={`pricing-page-card-${plan.name.toLowerCase()}`}
            >
              <div className="mb-6">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
                  <div>
                    <h3 className="uxa-h3 mb-0.5">{plan.name}</h3>
                    <p className="uxa-dense" style={{ color: 'var(--ink-2)', margin: 0 }}>{plan.type}</p>
                  </div>
                  {plan.badge && <span className="uxa-price-badge">{plan.badge}</span>}
                </div>
              </div>
              <div className="mb-6">
                <span className="font-bold text-3xl" style={{ color: 'var(--ink-1)', letterSpacing: '-0.04em' }}>
                  {plan.price}
                </span>
                <span className="uxa-dense" style={{ color: 'var(--ink-2)' }}>{plan.period}</span>
              </div>
              {/* The handoff replaces the CheckCircle icons with a hollow ring on
                  Free and a filled accent square on Starter -- the ranking is
                  carried by the marker, not by colouring the text. The markers
                  are ::before, so no icon element is rendered for a screen
                  reader to skip past. */}
              <ul className={`uxa-price-list--v2 mb-8 flex-1${plan.highlighted ? ' uxa-price-list--featured' : ''}`}>
                {plan.features.map((f) => (
                  <li key={f}>
                    <span className="uxa-dense" style={{ color: plan.highlighted ? 'var(--ink-1)' : 'var(--ink-2)' }}>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
              {/* Same CTA contract as every other primary on the site: signed
                  in goes straight to a new audit, signed out opens the login
                  popup. Both cards lead to the same place because both plans
                  start the same way: one free audit every month.

                  SKINS FOLLOW THE CARD GROUND. The Starter card was ink and
                  its CTA carried --on-dark (4a626a9). The handoff retires the
                  dark card for a white floated one, so the correct control is
                  the plain primary -- an ink fill with the white knob, which
                  is exactly what the handoff draws. --on-dark is dropped
                  because the dark ground is gone, not because the earlier fix
                  was wrong; on an ink card it is still required. */}
              {plan.contact ? (
                /* NOT the login/checkout flow. Agency cannot be bought, so its
                   CTA is a plain link to the contact page that already exists
                   — no new form, no payment session, no `login` call. It is
                   --secondary because the page's primary action is still the
                   free audit; a filled control here would compete with it. */
                <Link
                  to="/contact/"
                  className="uxa-submit uxa-submit--block uxa-submit--secondary"
                  data-testid={`pricing-page-cta-${plan.name.toLowerCase()}`}
                >
                  Get in touch
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              ) : user ? (
                <Link
                  to="/audit/new"
                  className={`uxa-submit uxa-submit--block${plan.highlighted ? '' : ' uxa-submit--secondary'}`}
                  data-testid={`pricing-page-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.highlighted ? 'Get a full audit' : 'Start free'}
                  {plan.highlighted ? (
                    <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                  ) : (
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  )}
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={login}
                  className={`uxa-submit uxa-submit--block${plan.highlighted ? '' : ' uxa-submit--secondary'}`}
                  data-testid={`pricing-page-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.highlighted ? 'Get a full audit' : 'Start free'}
                  {plan.highlighted ? (
                    <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                  ) : (
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>

        {/* The add-ons are ONE-TIME and the page says so. pricing.py:41 is
            explicit that they attach to an audit purchase and do not recur;
            describing white-label as "$25/mo" was a live misreading inside
            this project before the code was checked. */}
        <p
          className="uxa-body"
          style={{ maxWidth: 720, margin: 'var(--space-10) auto 0', textAlign: 'center', color: 'var(--ink-2)' }}
        >
          {`Two optional add-ons at checkout, each a one-time charge on that audit: `
            + `white-label reports for $${ADDON_PRICES.white_label.usd}, and a 30-minute `
            + `expert consultation for $${ADDON_PRICES.expert_call.usd}.`}
        </p>

      </section>

      {/* 1. THE FULL COMPARISON, below the two cards.
             Rows come from config/planFeatures.js, shared with the cards
             above. See PlanComparisonTable.jsx for why it reuses /compare/'s
             table rather than introducing a second one. */}
      <PlanComparisonTable />

      {/* 2. TESTIMONIAL — DELIBERATELY NOT BUILT.
             ────────────────────────────────────────────────────────────────
             The slot is here, in the layout, between the comparison and the
             closing CTA. It is empty because there is no real attributed
             quote to put in it, and this site has already had fabricated
             testimonials removed once.

             What would fill it: a quote with a named person, their role and
             their company, given knowingly for public use on this page. Not
             "a customer said", not an initial and a job title, not a
             paraphrase of something someone said in support. If that quote
             does not arrive, the correct outcome is deleting this comment,
             not softening the standard until something fits.

             Nothing renders here on purpose. A placeholder card reading
             "Testimonial coming soon" is a worse answer than a gap: it ships
             to production, it gets prerendered into the HTML, and it tells a
             buyer we could not find one customer to say something. */}

      {/* 3. ONE CTA. The closing band is the page's single action.
             THE PAGE USED TO END ON A GHOST LINK.
          Every other marketing route closes on the light .uxa-closing band --
          /, /ux-auditor/, /compare/, /use-cases/*, /about/, /blog/,
          /sample-report/, /mvp-validator/ -- and /pricing/ was the one that
          did not. Its last element was the tertiary sample-report link, which
          is background: transparent, border: none by design, so the region
          above the footer read as empty.

          The band adds no new token and no new class: .uxa-closing and
          .uxa-closing__grid already exist in landing.css, and the CTA is the
          same primary-plus-knob every other band uses. The sample-report link
          moves INTO the band and keeps its tertiary skin, exactly as it does
          inside /compare/'s band -- it must not compete with the primary,
          which is what test_the_link_never_competes_with_a_primary_cta pins
          across all five surfaces. */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>
            Start with the free one
          </h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 40px', maxWidth: 560 }}>
            {`Your free audit each month costs nothing and needs nothing but a URL. `
              + `Every audit after that is $${PLAN_PRICES.STARTER.usd}, once. `
              + `Free and Starter have no subscription.`}
          </p>
          <div style={{ position: 'relative', display: 'inline-block', isolation: 'isolate' }}>
            <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
              {user ? (
                <Link to="/audit/new" className="uxa-submit uxa-submit--auto" data-testid="pricing-page-closing-cta">
                  Run a free audit
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </Link>
              ) : (
                <button type="button" onClick={login} className="uxa-submit uxa-submit--auto" data-testid="pricing-page-closing-cta">
                  Run a free audit
                  <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                </button>
              )}
            </span>
          </div>
          {SAMPLE_REPORT_PUBLISHED && (
            <p className="uxa-sample-link">
              <Link to="/sample-report/" className="uxa-submit uxa-submit--auto uxa-submit--tertiary">
                {SAMPLE_REPORT_LINK_TEXT}
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
